import { FC, PropsWithChildren, ReactNode, useCallback } from 'react';
import { Flex, type FlexProps } from 'antd';
import styled from 'styled-components';
import { useResponsiveGlobalTokenValue } from '../../useResponsiveGlobalTokenValue';
import { ClearButton } from '../common/ClearButton';
import { useTableStoreValue } from '../TableContext';
import { Table as TableInstance } from '@tanstack/react-table';

export interface WithActionsProps extends PropsWithChildren {
  actions?: ReactNode | ((table: TableInstance<any>) => ReactNode);
  scrollContainer?: HTMLElement;
  hideClearFilters?: boolean;
  actionsInFront?: boolean;
  actionsWrapperProps?: FlexProps;
}

export const WithActions: FC<WithActionsProps> = (props) => {
  const {
    children,
    scrollContainer,
    hideClearFilters,
    actionsInFront,
    actionsWrapperProps,
  } = props;
  const [table] = useTableStoreValue('table');
  const [id] = useTableStoreValue('id');
  const responsivePaddingPx = useResponsiveGlobalTokenValue('padding');
  const paddingPx =
    scrollContainer?.id === 'detail-layout-main-content'
      ? responsivePaddingPx
      : 0;

  const clearAllFilters = useCallback(() => {
    table.getAllColumns().forEach((column) => column.setFilterValue(undefined));
  }, [table]);

  const filterCount = table
    .getAllColumns()
    .reduce((count, column) => (column.getIsFiltered() ? count + 1 : count), 0);

  const actions =
    typeof props.actions === 'function' ? props.actions(table) : props.actions;

  return (
    <Flex vertical gap="middle">
      {Boolean(actions || filterCount) && (
        <ActionsWrapper
          $padding={paddingPx}
          gap="small"
          justify="start"
          {...actionsWrapperProps}
        >
          {actionsInFront && actions}
          {Boolean(filterCount) && !hideClearFilters && (
            <ClearButton
              id={`${id}-clear-filters-button`}
              count={filterCount}
              onClick={clearAllFilters}
            />
          )}
          {!actionsInFront && actions}
        </ActionsWrapper>
      )}
      <div>{children}</div>
    </Flex>
  );
};

const ActionsWrapper = styled(Flex)<{ $padding: number }>`
  position: sticky;
  left: ${({ $padding }) => `${$padding}px`};
  right: ${({ $padding }) => `${$padding}px`};
`;
